import { supabase } from "@/lib/supabase";
import {
  validateImage,
  convertToWebP,
  processAndUploadImage,
  removeStoredImage,
} from "@/lib/image-processor";

/** Valida que el archivo sea una imagen con formato permitido. */
export function isValidProfileImage(file: File): boolean {
  return validateImage(file).ok;
}

/**
 * Redimensiona y comprime una imagen en el navegador (canvas) hasta un
 * tamaño máximo, devolviendo un blob WebP listo para subir.
 */
export function compressImage(file: File, maxDim: number): Promise<Blob> {
  return convertToWebP(file, { maxDim, quality: 0.85 });
}

/** Sube un blob (ya comprimido) a un bucket y devuelve su URL pública. */
export async function uploadBlob(
  bucket: "avatars" | "covers",
  userId: string,
  blob: Blob
): Promise<string> {
  const path = `${userId}/${Date.now()}.webp`;
  const { error } = await supabase.storage
    .from(bucket)
    .upload(path, blob, { contentType: "image/webp", upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

/** Comprime y sube una imagen a un bucket de Storage (WebP optimizado). */
export async function uploadProfileImage(
  bucket: "avatars" | "covers",
  userId: string,
  file: File
): Promise<string> {
  const result = await processAndUploadImage({
    file,
    bucket,
    folder: userId,
    kind: "profile",
    prefix: bucket === "avatars" ? "avatar" : "cover",
  });
  return result.url;
}

/** Elimina una imagen de Storage (y sus miniaturas) a partir de su URL. */
export async function removeProfileImage(
  bucket: "avatars" | "covers",
  userId: string,
  url: string
): Promise<void> {
  const fileName = url.split("/").pop();
  if (!fileName) return;
  await removeStoredImage(bucket, `${userId}/${fileName}`);
}

/**
 * Sanitiza la biografía: elimina etiquetas HTML y caracteres de control
 * para prevenir XSS e inyección de contenido ejecutable.
 */
export function sanitizeBio(text: string): string {
  return text
    .replace(/<[^>]*>/g, "")
    .replace(/[\t\r\n]+/g, " ")
    .trim();
}

/** Valida y normaliza un username (minúsculas, caracteres permitidos). */
export function normalizeUsername(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9._-]/g, "");
}

export function isValidUsername(username: string): boolean {
  return /^[a-z0-9._-]{3,30}$/.test(username);
}

function relativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years >= 1) return `${years} ${years === 1 ? "año" : "años"}`;
  if (months >= 1) return `${months} ${months === 1 ? "mes" : "meses"}`;
  if (days >= 1) return `${days} ${days === 1 ? "día" : "días"}`;
  if (hours >= 1) return `${hours} ${hours === 1 ? "hora" : "horas"}`;
  return `${Math.max(minutes, 1)} ${minutes === 1 ? "minuto" : "minutos"}`;
}

/** Calcula "Miembro desde [fecha]" y "Miembro desde hace X". */
export function memberSinceText(createdAt: string | null): {
  date: string;
  relative: string;
} {
  if (!createdAt) return { date: "", relative: "" };
  const date = new Date(createdAt);
  const dateText = date.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  return { date: dateText, relative: relativeTime(date) };
}